import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { SelectWithAdd } from "@/components/SelectWithAdd";
import OptionCombobox from "@/components/OptionCombobox";
import SubstrateCombobox from "@/components/SubstrateCombobox";
import { Pencil } from "lucide-react";
import { toast } from "sonner";

// Same cheat sheet as the Master Job Card's cylinder-repeat picker (MasterJobCardForm.jsx) —
// picking teeth or mm fills the other from the matching row.
const CYL_REPEAT_ROWS = [
  { teeth: "73", mm: "231.775" }, { teeth: "80", mm: "254" }, { teeth: "84", mm: "266.7" },
  { teeth: "88", mm: "279.4" }, { teeth: "90", mm: "285.75" }, { teeth: "92", mm: "292.1" },
  { teeth: "95", mm: "301.625" }, { teeth: "97", mm: "307.975" }, { teeth: "98", mm: "311.15" },
  { teeth: "100", mm: "317.5" }, { teeth: "104", mm: "330.2" }, { teeth: "111", mm: "352.425" },
  { teeth: "116", mm: "368.3" }, { teeth: "120", mm: "381" }, { teeth: "128", mm: "406.4" },
  { teeth: "136", mm: "431.8" }, { teeth: "160", mm: "508" }, { teeth: "182", mm: "577.85" },
];

const NUM_FIELDS = [
  "label_length_mm", "label_width_mm", "gsm", "no_of_colors", "labels_per_roll", "labels_per_sheet",
  "ups_across", "ups_along", "no_of_ups", "along_gap_mm", "stock_width_mm", "planned_good_meter",
  "planned_waste_meter", "excess_qty", "makeready_hours",
];

function blankForm(job) {
  return {
    label_length_mm: job.label_length_mm ?? "", label_width_mm: job.label_width_mm ?? "",
    die_size: job.die_size || "", die_cutting: job.die_cutting || "",
    die_box_number: job.die_box_number || "", plate_box_number: job.plate_box_number || "",
    core_diameter: job.core_diameter || "",
    cylinder_teeth: job.cylinder_teeth || job.teeth || "", cylinder_mm: job.cylinder_mm || "",
    substrate_type: job.substrate_type || "", substrate_code: job.substrate_code || "",
    substrate_description: job.substrate_description || "", gsm: job.gsm ?? "",
    manufactured_by: job.manufactured_by || "", supplier: job.supplier || "",
    no_of_colors: job.no_of_colors ?? "", colour_details: job.colour_details || "",
    print_type: job.print_type || "", color_matching: job.color_matching || "",
    finishing_details: job.finishing_details || "", varnish_type: job.varnish_type || "",
    embellishment: job.embellishment || "", shade_card: job.shade_card || "",
    form_of_supply: job.form_of_supply || "",
    labels_per_roll: job.labels_per_roll ?? "", labels_per_sheet: job.labels_per_sheet ?? "",
    ups_across: job.ups_across ?? "", ups_along: job.ups_along ?? "", no_of_ups: job.no_of_ups ?? "",
    along_gap_mm: job.along_gap_mm ?? "", stock_width_mm: job.stock_width_mm ?? "",
    planned_good_meter: job.planned_good_meter ?? "", planned_waste_meter: job.planned_waste_meter ?? "",
    excess_qty: job.excess_qty ?? "", makeready_hours: job.makeready_hours ?? "",
  };
}

function F({ label, value, onChange, type = "text", mono, full, testId }) {
  return (
    <label className={`text-xs ${full ? "col-span-2 md:col-span-3" : ""}`}>
      <span className="label-caps block mb-1">{label}</span>
      <Input data-testid={testId} type={type} value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`rounded-sm h-9 text-sm ${mono ? "font-mono-num" : ""}`} />
    </label>
  );
}

function Area({ label, value, onChange, full, testId }) {
  return (
    <label className={`text-xs ${full ? "col-span-2 md:col-span-3" : ""}`}>
      <span className="label-caps block mb-1">{label}</span>
      <Textarea data-testid={testId} value={value} onChange={(e) => onChange(e.target.value)} className="rounded-sm text-sm min-h-[60px]" />
    </label>
  );
}

function Pick({ label, optionsKey, value, onChange, testId }) {
  return (
    <div className="text-xs">
      <span className="label-caps block mb-1">{label}</span>
      <SelectWithAdd optionsKey={optionsKey} value={value} onChange={onChange} placeholder="Pick or add…" testId={testId} />
    </div>
  );
}

function Combo({ label, optionsKey, value, onChange, testId }) {
  return (
    <div className="text-xs">
      <span className="label-caps block mb-1">{label}</span>
      <OptionCombobox optionsKey={optionsKey} value={value || ""} onChange={onChange} testId={testId} />
    </div>
  );
}

// Real stock widths for this substrate, so Stock Width is picked from what's actually available
// rather than guessed — checked against real jobs, no formula reliably predicts this figure (the
// implied edge margin for a single-up label ranged from ~19mm to ~290mm across real jobs), so it's
// a stock-availability decision, not a calculation. Clicking a chip fills Stock Width with it.
function AvailableWidths({ paperCode, onPick }) {
  const [widths, setWidths] = useState([]);
  useEffect(() => {
    if (!paperCode) { setWidths([]); return; }
    api.get("/stores/widths", { params: { paper_code: paperCode } }).then(({ data }) => setWidths(data)).catch(() => setWidths([]));
  }, [paperCode]);
  if (!paperCode || widths.length === 0) return null;
  return (
    <div className="mt-1.5 flex flex-wrap gap-1.5">
      <span className="text-[10px] text-neutral-400 uppercase tracking-wider self-center">In stock:</span>
      {widths.map((w) => (
        <button
          key={w.item_code}
          type="button"
          data-testid={`pe-width-chip-${w.width}`}
          onClick={() => onPick(w.width)}
          title={`${w.balance.toLocaleString("en-IN")} ${w.uom === "KG" ? "kg" : "m"} across ${w.rolls} roll(s)`}
          className="text-[11px] font-mono-num px-1.5 py-0.5 border border-neutral-300 rounded-sm hover:border-[#FF5A00] hover:text-[#FF5A00]"
        >
          {w.width}mm
        </button>
      ))}
    </div>
  );
}

/** Edit affordance for Order Detail → Planning Spec: the same pick-or-add lists as New Job /
 *  Master Job Card, so staff can fill in or correct a field without opening the full MJC tab.
 *  Independent of ThinCardBanner's "Fill the empty fields" auto-fill, which this never touches. */
export default function EditPlanningSpecDialog({ job, onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const syncCyl = (col, v) => {
    const row = CYL_REPEAT_ROWS.find((r) => r[col] === v);
    setForm((f) => ({
      ...f,
      cylinder_teeth: col === "teeth" ? v : (row ? row.teeth : f.cylinder_teeth),
      cylinder_mm: col === "mm" ? v : (row ? row.mm : f.cylinder_mm),
    }));
  };
  // A job carrying a substrate code but not its linked fields (common on register-imported jobs,
  // saved before the Substrate Code picker was in place) gets them backfilled from the substrate
  // master once the dialog opens — same data picking it fresh would have set.
  useEffect(() => {
    if (!open || !form?.substrate_code || form.substrate_description) return;
    api.get("/substrates", { params: { q: form.substrate_code, limit: 10 } })
      .then(({ data }) => {
        const s = data.find((x) => String(x.code).trim().toUpperCase() === String(form.substrate_code).trim().toUpperCase());
        if (!s) return;
        setForm((f) => (f.substrate_description ? f : {
          ...f,
          substrate_description: s.description || f.substrate_description,
          substrate_type: f.substrate_type || s.substrate_type,
          gsm: f.gsm === "" || f.gsm == null ? s.gsm : f.gsm,
          manufactured_by: f.manufactured_by || s.manufacturer,
        }));
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form?.substrate_code]);

  // Same auto-calc as the New Job / Master Job Card form: Die Size from the label's own length x
  // width (a die is built to that exact size — verified against real jobs), Stock Width from label
  // width + gap across x ups across + a fixed trim allowance (a rough starting figure only — pick an
  // "In stock" chip for the real decision), Required Metres from order qty / ups per repeat x
  // cylinder (verified against the master Planning workbook's own figures). Only fills while the
  // field is empty or still equals what this effect last set — typing over either, or a value already
  // saved on the job, is respected.
  const derived = useRef({});
  useEffect(() => {
    if (!open || !form) return;
    setForm((f) => {
      const next = {};
      const upsAcross = Number(f.ups_across) || 0;
      const upsAlong = Number(f.ups_along) || 0;
      const ups = upsAcross * upsAlong;
      const labelW = Number(f.label_width_mm) || 0;
      const labelL = Number(f.label_length_mm) || 0;
      const gapAcross = Number(job.gap_across_mm) || 0;
      const width = labelW > 0 && upsAcross > 0 ? Math.round((labelW + gapAcross) * upsAcross + 12) : 0;
      const qty = Number(job.quantity) || 0;
      const cylinderMm = Number(f.cylinder_mm) || 0;
      const goodMeter = qty > 0 && ups > 0 && cylinderMm > 0
        ? Math.round((Math.ceil(qty / ups) * cylinderMm / 1000) * 10) / 10 : 0;
      const want = { stock_width_mm: width, planned_good_meter: goodMeter };
      for (const [k, v] of Object.entries(want)) {
        const free = f[k] === "" || f[k] == null || String(f[k]) === String(derived.current[k]);
        if (v > 0 && free && String(f[k]) !== String(v)) { next[k] = v; derived.current[k] = v; }
      }
      const dieSize = labelW > 0 && labelL > 0 ? `${labelW}X${labelL}` : "";
      const dieFree = f.die_size === "" || f.die_size == null || f.die_size === derived.current.die_size;
      if (dieSize && dieFree && f.die_size !== dieSize) { next.die_size = dieSize; derived.current.die_size = dieSize; }
      return Object.keys(next).length ? { ...f, ...next } : f;
    });
  }, [open, form?.label_width_mm, form?.label_length_mm, form?.ups_across, form?.ups_along, form?.cylinder_mm, job.gap_across_mm, job.quantity]);

  const pickSubstrate = (s) => setForm((f) => ({
    ...f,
    substrate_code: s.code,
    substrate_description: s.description || f.substrate_description,
    substrate_type: s.substrate_type || f.substrate_type,
    gsm: s.gsm ?? f.gsm,
    manufactured_by: s.manufacturer || f.manufactured_by,
  }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      for (const [k, v] of Object.entries(form)) {
        if (v === "" || v == null) continue;
        payload[k] = NUM_FIELDS.includes(k) ? Number(v) : v;
      }
      const { data } = await api.patch(`/jobs/${job.id}`, payload);
      toast.success("Planning spec updated");
      setOpen(false);
      onSaved && onSaved(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setForm(blankForm(job)); }}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="rounded-sm gap-1.5" data-testid="planning-edit-btn">
          <Pencil size={13} /> Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl rounded-sm max-h-[88vh] overflow-y-auto" data-testid="planning-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit Planning Spec</DialogTitle>
          <DialogDescription>Pick or add, same lists as New Job / Master Job Card. Leave a field blank to leave it as it is.</DialogDescription>
        </DialogHeader>
        {form && (
          <div className="space-y-5">
            <div>
              <div className="label-caps text-[10px] mb-2 text-neutral-500">Label Dimensions & Die</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <F label="Label Length (mm)" type="number" value={form.label_length_mm} onChange={(v) => upd("label_length_mm", v)} mono testId="pe-label_length_mm" />
                <F label="Label Width (mm)" type="number" value={form.label_width_mm} onChange={(v) => upd("label_width_mm", v)} mono testId="pe-label_width_mm" />
                <Combo label="Die Size" optionsKey="die_size" value={form.die_size} onChange={(v) => upd("die_size", v)} testId="pe-die_size" />
                <F label="Die Cutting" value={form.die_cutting} onChange={(v) => upd("die_cutting", v)} testId="pe-die_cutting" />
                <F label="Die Box Number" value={form.die_box_number} onChange={(v) => upd("die_box_number", v)} mono testId="pe-die_box_number" />
                <F label="Plate Box Number" value={form.plate_box_number} onChange={(v) => upd("plate_box_number", v)} mono testId="pe-plate_box_number" />
                <F label="Core Diameter" value={form.core_diameter} onChange={(v) => upd("core_diameter", v)} mono testId="pe-core_diameter" />
                <Pick label="Cylinder Teeth" optionsKey="cylinder_teeth" value={form.cylinder_teeth} onChange={(v) => syncCyl("teeth", v)} testId="pe-cylinder_teeth" />
                <Pick label="Cylinder (mm)" optionsKey="cylinder_mm" value={form.cylinder_mm} onChange={(v) => syncCyl("mm", v)} testId="pe-cylinder_mm" />
              </div>
            </div>

            <div>
              <div className="label-caps text-[10px] mb-2 text-neutral-500">Substrate & Colors</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <Pick label="Substrate Type" optionsKey="substrate_type" value={form.substrate_type} onChange={(v) => upd("substrate_type", v)} testId="pe-substrate_type" />
                <div className="text-xs">
                  <span className="label-caps block mb-1">Substrate Code</span>
                  <SubstrateCombobox
                    testId="pe-substrate_code"
                    value={form.substrate_code ? {
                      code: form.substrate_code, description: form.substrate_description,
                      gsm: form.gsm, manufacturer: form.manufactured_by, substrate_type: form.substrate_type,
                    } : null}
                    onPick={pickSubstrate}
                  />
                </div>
                <F label="GSM" type="number" value={form.gsm} onChange={(v) => upd("gsm", v)} mono testId="pe-gsm" />
                <F label="Substrate Description" value={form.substrate_description} onChange={(v) => upd("substrate_description", v)} full testId="pe-substrate_description" />
                <Pick label="Manufactured By" optionsKey="manufacturer" value={form.manufactured_by} onChange={(v) => upd("manufactured_by", v)} testId="pe-manufactured_by" />
                <F label="Supplier" value={form.supplier} onChange={(v) => upd("supplier", v)} testId="pe-supplier" />
                <F label="No. of Colors" type="number" value={form.no_of_colors} onChange={(v) => upd("no_of_colors", v)} mono testId="pe-no_of_colors" />
                <Combo label="Print Type" optionsKey="print_type" value={form.print_type} onChange={(v) => upd("print_type", v)} testId="pe-print_type" />
                <Pick label="Color Matching" optionsKey="color_matching" value={form.color_matching} onChange={(v) => upd("color_matching", v)} testId="pe-color_matching" />
                <Pick label="Finishing" optionsKey="finishing_details" value={form.finishing_details} onChange={(v) => upd("finishing_details", v)} testId="pe-finishing_details" />
                <Pick label="Varnish" optionsKey="varnish_type" value={form.varnish_type} onChange={(v) => upd("varnish_type", v)} testId="pe-varnish_type" />
                <Pick label="Embellishment" optionsKey="embellishment" value={form.embellishment} onChange={(v) => upd("embellishment", v)} testId="pe-embellishment" />
                <F label="Shade Card" value={form.shade_card} onChange={(v) => upd("shade_card", v)} testId="pe-shade_card" />
                <Pick label="Form of Supply" optionsKey="form_of_supply" value={form.form_of_supply} onChange={(v) => upd("form_of_supply", v)} testId="pe-form_of_supply" />
                <Area label="Colour Details" value={form.colour_details} onChange={(v) => upd("colour_details", v)} full testId="pe-colour_details" />
              </div>
            </div>

            <div>
              <div className="label-caps text-[10px] mb-2 text-neutral-500">Layout & Planned Values</div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <F label="Labels / Roll" type="number" value={form.labels_per_roll} onChange={(v) => upd("labels_per_roll", v)} mono testId="pe-labels_per_roll" />
                <F label="Labels / Sheet" type="number" value={form.labels_per_sheet} onChange={(v) => upd("labels_per_sheet", v)} mono testId="pe-labels_per_sheet" />
                <F label="Ups Across" type="number" value={form.ups_across} onChange={(v) => upd("ups_across", v)} mono testId="pe-ups_across" />
                <F label="Ups Along" type="number" value={form.ups_along} onChange={(v) => upd("ups_along", v)} mono testId="pe-ups_along" />
                <F label="No. of Ups Planned / Repeat" type="number" value={form.no_of_ups} onChange={(v) => upd("no_of_ups", v)} mono testId="pe-no_of_ups" />
                <F label="Along Gap (mm)" type="number" value={form.along_gap_mm} onChange={(v) => upd("along_gap_mm", v)} mono testId="pe-along_gap_mm" />
                <div>
                  <F label="Stock Width (mm)" type="number" value={form.stock_width_mm} onChange={(v) => upd("stock_width_mm", v)} mono testId="pe-stock_width_mm" />
                  {Number(form.label_width_mm) > 0 && Number(form.ups_across) > 0 && (
                    <div className="text-[10px] text-neutral-400 mt-1">
                      Calculated: (label {form.label_width_mm}mm + gap {job.gap_across_mm || 0}mm) × {form.ups_across} ups + 12mm trim
                      — pick an "In stock" width below rather than relying on this alone.
                    </div>
                  )}
                  <AvailableWidths paperCode={form.substrate_code} onPick={(w) => upd("stock_width_mm", w)} />
                </div>
                <div>
                  <F label="Planned Good (m)" type="number" value={form.planned_good_meter} onChange={(v) => upd("planned_good_meter", v)} mono testId="pe-planned_good_meter" />
                  {Number(job.quantity) > 0 && Number(form.ups_across) > 0 && Number(form.ups_along) > 0 && Number(form.cylinder_mm) > 0 && (
                    <div className="text-[10px] text-neutral-400 mt-1">
                      Calculated: ceil({job.quantity} qty ÷ {Number(form.ups_across) * Number(form.ups_along)} ups per repeat) × {form.cylinder_mm}mm cylinder
                    </div>
                  )}
                </div>
                <F label="Planned Waste (m)" type="number" value={form.planned_waste_meter} onChange={(v) => upd("planned_waste_meter", v)} mono testId="pe-planned_waste_meter" />
                <F label="Excess Qty" type="number" value={form.excess_qty} onChange={(v) => upd("excess_qty", v)} mono testId="pe-excess_qty" />
                <F label="Makeready (hr)" type="number" value={form.makeready_hours} onChange={(v) => upd("makeready_hours", v)} mono testId="pe-makeready_hours" />
              </div>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
          <Button type="button" data-testid="planning-edit-save" disabled={saving || !form} className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]" onClick={save}>
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
